import { useState } from 'react';
import { useLanguageStore } from 'common/src/stores/useLanguageStore';
import {
  AVAILABLE_LANGUAGES,
  LANGUAGE_NAMES,
  LANGUAGE_FLAGS,
  Language,
} from '../../../common/src/i18n/types';
import { Icon } from '@iconify/react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@nextui-org/react';

interface LanguageSelectorProps {
  className?: string;
  variant?: 'button' | 'minimal' | 'text' | 'flags';
}

export function LanguageSelector({
  className = '',
  variant = 'minimal',
}: LanguageSelectorProps) {
  const { language: currentLanguage, setLanguage } = useLanguageStore();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguageName = LANGUAGE_NAMES[currentLanguage];
  const currentLanguageFlag = LANGUAGE_FLAGS[currentLanguage];

  const handleLanguageChange = (language: string) => {
    setLanguage(language as Language);
    setIsOpen(false);
  };

  if (variant === 'flags') {
    const otherLang: Language = currentLanguage === 'es' ? 'en' : 'es';

    return (
      <Button
        isIconOnly
        size='md'
        variant='light'
        onPress={() => handleLanguageChange(otherLang)}
        aria-label={LANGUAGE_NAMES[currentLanguage]}
        className={className}
      >
        <span className='text-xs font-bold'>
          {currentLanguage?.toUpperCase()}
        </span>
      </Button>
    );
  }

  if (variant === 'text') {
    const availableLanguages = ['en', 'es'];

    return (
      <Dropdown isOpen={isOpen} onOpenChange={setIsOpen}>
        <DropdownTrigger>
          <Button
            variant='flat'
            className={`w-full justify-start ${className}`}
            startContent={
              <svg
                className='w-5 h-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z'
                />
              </svg>
            }
          >
            {currentLanguageName}
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          selectedKeys={new Set([currentLanguage])}
          onAction={(key) => handleLanguageChange(key as string)}
          aria-label='Language selection'
        >
          {Object.entries(AVAILABLE_LANGUAGES)
            .filter(([key, value]) => availableLanguages.includes(value))
            .map(([key, value]) => (
              <DropdownItem key={value}>
                {LANGUAGE_NAMES[value as Language]}
              </DropdownItem>
            ))}
        </DropdownMenu>
      </Dropdown>
    );
  }

  if (variant === 'minimal') {
    return (
      <Dropdown isOpen={isOpen} onOpenChange={setIsOpen}>
        <DropdownTrigger>
          <Button
            variant='light'
            radius='full'
            size='md'
            className={`rounded-full text-default-600 hover:bg-default-200 ${className}`}
            isIconOnly
            aria-label={`Language: ${currentLanguageName}`}
          >
            <span className='text-xs font-bold'>
              {currentLanguage?.toUpperCase()}
            </span>
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          selectedKeys={new Set([currentLanguage])}
          onAction={(key) => handleLanguageChange(key as string)}
        >
          {Object.entries(AVAILABLE_LANGUAGES).map(([key, value]) => {
            const flagIcon = LANGUAGE_FLAGS[value as Language];
            return (
              <DropdownItem
                key={value}
                className='hover:bg-default-100 transition-colors duration-200'
                startContent={
                  flagIcon ? (
                    <Icon icon={flagIcon} className='w-8 h-8 drop-shadow-md' />
                  ) : (
                    <Icon
                      icon='material-symbols:language'
                      className='text-lg'
                    />
                  )
                }
              >
                {LANGUAGE_NAMES[value as Language]}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      </Dropdown>
    );
  }

  return (
    <Dropdown isOpen={isOpen} onOpenChange={setIsOpen}>
      <DropdownTrigger>
        <Button
          variant='flat'
          className={`flex items-center gap-2 hover:scale-105 transition-transform duration-200 ${className}`}
          startContent={
            currentLanguageFlag ? (
              <Icon
                icon={currentLanguageFlag}
                className='w-10 h-10 drop-shadow-md'
              />
            ) : (
              <Icon icon='material-symbols:language' className='text-lg' />
            )
          }
          endContent={
            <Icon
              icon='material-symbols:keyboard-arrow-down'
              className={`text-lg transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                }`}
            />
          }
        >
          {currentLanguageName}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        selectedKeys={new Set([currentLanguage])}
        onAction={(key) => handleLanguageChange(key as string)}
      >
        {Object.entries(AVAILABLE_LANGUAGES).map(([key, value]) => {
          const flagIcon = LANGUAGE_FLAGS[value as Language];
          return (
            <DropdownItem
              key={value}
              className='hover:bg-default-50 transition-colors duration-200'
              startContent={
                flagIcon ? (
                  <Icon icon={flagIcon} className='w-8 h-8 drop-shadow-md' />
                ) : (
                  <Icon icon='material-symbols:language' className='text-lg' />
                )
              }
            >
              {LANGUAGE_NAMES[value as Language]}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
}
